import type { GameState, LevelDef, RunSummary } from '@full-elevator/sim';
import { formatDuration } from '@full-elevator/sim';
import { floorName, groundOf } from '../floorNames.js';

/**
 * One sentence naming the actual cause of the result.
 *
 * The results screen has one job: answer "why did I get that?". A generic
 * "well done" teaches nothing, so every branch here points at something the
 * player did or failed to do. Words only: every count is the sim's.
 */

/** Who was lost in ways the summary does not count apart (ADR-0029, ADR-0031). */
export interface Lost {
  /** The emergency's person, if one was lost. */
  readonly crew: string | null;
  /** Commuters who missed their train: counted with late deliveries, but said apart. */
  readonly trains: number;
  /** Couriers whose bar ran out: counted with late deliveries, and named when they are all of them. */
  readonly couriers: number;
  /** The hotel's departures missed (ADR-0051), by who missed them. */
  readonly missed?: Readonly<Record<string, number>>;
}

const NOBODY: Lost = { crew: null, trains: 0, couriers: 0 };

/** What missing each of the hotel's departures is called (ADR-0051). */
const MISSED: Readonly<Record<string, string>> = {
  shuttleGuest: 'missed the shuttle',
  breakfastGuest: 'missed breakfast',
  newlywed: 'missed the wedding car',
};

/** The losses the summary does not tell apart, read off the run's own people. */
export function lostIn(state: GameState): Lost {
  return {
    crew: state.passengers.find((p) => p.critical && p.state === 'abandoned')?.archetype ?? null,
    trains: state.passengers.filter(
      (p) => p.archetype === 'commuter' && p.lostReason === 'deadline',
    ).length,
    couriers: state.passengers.filter(
      (p) => p.archetype === 'courier' && p.lostReason === 'deadline',
    ).length,
    missed: Object.fromEntries(
      Object.keys(MISSED).map((who) => [
        who,
        state.passengers.filter((p) => p.archetype === who && p.lostReason === 'deadline').length,
      ]),
    ),
  };
}

/**
 * Everyone given up on, by their own cause. A guest turned away at a full
 * floor is counted by the sim as someone who gave up (`turnedAway` is kept
 * apart in the summary), and the hotel's losses read "gave up waiting" beside
 * an average wait of 0.2 s (writer review, 2026-10-26).
 */
export function gaveUpWaiting(summary: RunSummary): number {
  return Math.max(0, summary.abandoned - summary.turnedAway);
}

/** Each cause of a loss, as a clause that starts with its count. */
function causes(summary: RunSummary, lost: Lost): string[] {
  const out: string[] = [];
  const turned = summary.turnedAway;
  if (turned > 0)
    out.push(`${turned} ${turned === 1 ? 'was' : 'were'} turned away at a full floor`);
  const gaveUp = gaveUpWaiting(summary);
  if (gaveUp > 0) out.push(`${gaveUp} gave up waiting`);
  // A bar that ran out, the courier's card's own words: "deadline" was above
  // the reader, and a courier is lost even in the lift (qa-lead review).
  const missed = Object.entries(lost.missed ?? {}).filter(([, count]) => count > 0);
  const late = summary.couriersLate - lost.trains - missed.reduce((sum, [, n]) => sum + n, 0);
  if (late > 0) {
    const who = late === lost.couriers ? (late === 1 ? ' courier' : ' couriers') : '';
    out.push(`${late}${who} ran out of time`);
  }
  if (lost.trains > 0) out.push(`${lost.trains} missed their train`);
  for (const [who, count] of missed) out.push(`${count} ${MISSED[who]}`);
  return out;
}

/** "a", "a and b", "a, b and c" (or "a, then b, then c"). */
function listOf(items: readonly string[], joiner: 'and' | 'then' = 'and'): string {
  if (items.length <= 1) return items.join('');
  if (joiner === 'then') return items.join(', then ');
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}

export function explainOutcome(
  summary: RunSummary,
  level: LevelDef,
  stars: number,
  lost: Lost = NOBODY,
): string {
  if (summary.status !== 'won') return explainLoss(summary, lost, level);
  if (stars === 3) return 'Nothing wasted. That is the route.';

  if (summary.metric === 'service') {
    const short = Math.max(0, level.stars.three - summary.service);
    const why = causes(summary, lost).map((cause) => ` ${cause}.`);
    return `Finished ${short} points short of three stars.${why.join('')}`;
  }

  if (summary.metric === 'trips') {
    const extra = Math.max(0, summary.trips - level.stars.three);
    return `Finished in ${summary.trips} trips: ${extra} more than a three-star plan.`;
  }

  const behind = formatDuration(Math.max(0, summary.elapsedMs - level.stars.three));
  return `Finished ${behind} slower than a three-star shift.`;
}

/**
 * How many points a shift graded on points needed past what it got, once enough
 * people were home: then the points are all it was lost on. Zero otherwise.
 */
function pointsShort(summary: RunSummary, level: LevelDef | undefined): number {
  const goal = level?.objective;
  if (goal?.kind !== 'serviceAbove' || summary.delivered < goal.count) return 0;
  return Math.max(0, goal.minService - summary.service);
}

/**
 * A lost shift's cause, first and in a few words (the owner, PLAYTEST_LOG
 * 2026-10-30): "you can't tell exactly why you lost". An icon, the cause, and
 * where it helps the count against the goal. The sentence under it says the rest.
 */
export interface LossCause {
  readonly icon: 'clock' | 'trip' | 'lost' | 'star';
  readonly text: string;
  readonly figure: string | null;
}

export function lossCause(summary: RunSummary, level?: LevelDef, lost: Lost = NOBODY): LossCause {
  if (summary.endReason === 'emergency') {
    const police = lost.crew === 'police' || lost.crew === 'arrest';
    return {
      icon: 'clock',
      text: police ? 'Too late for the police' : 'Too late for the patient',
      figure: null,
    };
  }
  if (summary.endReason === 'out-of-trips')
    return { icon: 'trip', text: 'Out of trips', figure: null };
  if (pointsShort(summary, level) > 0 && level?.objective.kind === 'serviceAbove') {
    return {
      icon: 'star',
      text: 'Not enough points',
      figure: `${summary.service} / ${level.objective.minService}`,
    };
  }
  if (summary.endReason === 'too-many-lost' || summary.endReason === 'objective-impossible') {
    return { icon: 'lost', text: 'Too many lost', figure: null };
  }
  return { icon: 'clock', text: 'Out of time', figure: homeOf(summary, level) };
}

/** People home against the people the goal needed, where the goal counts them. */
function homeOf(summary: RunSummary, level: LevelDef | undefined): string | null {
  const goal = level?.objective;
  if (!goal || goal.kind === 'survive') return null;
  const needed =
    goal.kind === 'deliverN' || goal.kind === 'serviceAbove' ? goal.count : summary.totalPassengers;
  return `${summary.delivered} / ${needed}`;
}

/** Why a shift that was not won ended, naming the clock that ran out. */
export function explainLoss(summary: RunSummary, lost: Lost = NOBODY, level?: LevelDef): string {
  if (summary.endReason === 'emergency') {
    // Which clock it was, by who it was for (ADR-0029).
    return lost.crew === 'police' || lost.crew === 'arrest'
      ? 'The police did not get their arrest down in time — an emergency ends the shift.'
      : 'The paramedics did not get their patient out in time — an emergency ends the shift.';
  }
  if (summary.endReason === 'out-of-trips') {
    // A puzzle shift (ADR-0037): the plan ran out before the people did. The
    // first floors of one that works, so the next try can start right
    // (`54-moving-in` was lost three times running on the same plan).
    const start = level?.plan?.slice(0, 3).map((f) => floorName(f, groundOf(level.buildingId)));
    return start && start.length > 0
      ? `A plan that works starts: ${listOf(start, 'then')}.`
      : 'Some were still not home. Try grouping them by floor.';
  }
  const short = pointsShort(summary, level);
  if (short > 0) {
    const why = causes(summary, lost).map((cause) => ` ${cause}.`);
    return `${short} points short of the goal.${why.join('')}`;
  }
  if (summary.endReason === 'too-many-lost') {
    const why = causes(summary, lost);
    return `${listOf(why.length > 0 ? why : ['Too many were lost'])}, more than the shift allows.`;
  }
  if (summary.endReason !== 'objective-impossible') {
    return 'People were still waiting when the clock ran out.';
  }
  // Name which clock ran out. "Too many people gave up" is wrong and confusing
  // when what actually happened is that a courier's bar ran out. And no
  // "target" or "run": the goal card says "goal" and "shift" (writer review).
  const why = causes(summary, lost);
  const said = why.length > 0 ? listOf(why) : 'Too many people were lost';
  return `${said}. The goal can’t be reached now, so the shift is over.`;
}
